import React from "react";
import s from "./LeadSystemVisual.module.scss";

const I: Record<string, React.ReactNode> = {
  phone: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2.5" />
      <path d="M11 18h2" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v8M9.5 10h3.5a1.8 1.8 0 0 1 0 3.6H10a1.8 1.8 0 0 0 0 3.6h3.5" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v4a5 5 0 0 1-10 0z" />
      <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" />
      <path d="M10 14h4M9 20h6M12 14v6" />
    </>
  ),
  capture: (
    <>
      <path d="M4 14v5h16v-5" />
      <path d="M12 3v10m0 0l-4-4m4 4l4-4" />
    </>
  ),
  ai: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M9 3v2M15 3v2M9 19v2M15 19v2M3 9h2M3 15h2M19 9h2M19 15h2" />
    </>
  ),
  won: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6 7-11a7 7 0 0 0-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  dash: (
    <>
      <path d="M3 21h18" />
      <path d="M6 16l3-4 3 2 4-6" />
    </>
  ),
};

interface Node {
  title: string;
  sub: string;
  icon: string;
  color: string;
  stage?: boolean;
}

const TOP: Node[] = [
  { title: "Mobile app", sub: "Capture & tasks", icon: "phone", color: "#06b6d4" },
  { title: "Incentive engine", sub: "Rewards & payouts", icon: "coin", color: "#f59e0b" },
  { title: "Leaderboard", sub: "Top performers", icon: "trophy", color: "#ec4899" },
];

const STAGE: Node[] = [
  { title: "Lead capture", sub: "Store footfall", icon: "capture", color: "#06b6d4", stage: true },
  { title: "AI conversion", sub: "Smart lead scoring", icon: "ai", color: "#6366f1", stage: true },
  { title: "Conversion", sub: "Won customers", icon: "won", color: "#8b5cf6", stage: true },
];

const BOTTOM: Node[] = [
  { title: "Geo footfall", sub: "Location sims", icon: "pin", color: "#6366f1" },
  { title: "Real-time dashboard", sub: "Live KPIs & alerts", icon: "dash", color: "#10b981" },
];

const FUNNEL = [
  { label: "Leads captured", value: "12,480", width: 100, color: "#22d3ee" },
  { label: "Qualified", value: "6,240", width: 82, color: "#38bdf8" },
  { label: "Engaged", value: "2,990", width: 64, color: "#6366f1" },
  { label: "Converted", value: "1,180", width: 46, color: "#8b5cf6" },
];

const NodeCard = ({ node }: { node: Node }) => (
  <div
    className={`${s.node} ${node.stage ? s.stageNode : ""}`}
    style={{ "--c": node.color } as React.CSSProperties}
  >
    {node.stage && <span className={s.stageBadge}>STAGE</span>}
    <span className={s.nodeIcon}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {I[node.icon]}
      </svg>
    </span>
    <span className={s.nodeText}>
      <span className={s.nodeTitle}>{node.title}</span>
      <span className={s.nodeSub}>{node.sub}</span>
    </span>
  </div>
);

const VConns = ({ cols }: { cols: number }) => (
  <div className={s.vconns}>
    {Array.from({ length: 3 }).map((_, i) => (
      <span key={i} className={i < cols ? s.vline : s.vempty} />
    ))}
  </div>
);

export const LeadSystemVisual = () => (
  <div className={s.panel}>
    <div className={s.panelHead}>
      <span className={s.panelTitle}>Lead Management System</span>
      <span className={s.panelSub}>Retail store · capture → convert → scale</span>
    </div>

    <div className={s.grid}>
      {TOP.map((n) => (
        <NodeCard node={n} key={n.title} />
      ))}
    </div>

    <VConns cols={3} />

    <div className={`${s.grid} ${s.stageRow}`}>
      <span className={s.flowLine} aria-hidden="true">
        <span className={s.flowPulse} />
      </span>
      {STAGE.map((n) => (
        <NodeCard node={n} key={n.title} />
      ))}
    </div>

    <VConns cols={2} />

    <div className={s.grid}>
      {BOTTOM.map((n) => (
        <NodeCard node={n} key={n.title} />
      ))}
      <span />
    </div>

    <div className={s.funnel}>
      <span className={s.funnelTitle}>Lead Conversion Funnel</span>
      {FUNNEL.map((f) => (
        <div
          className={s.funnelBar}
          key={f.label}
          style={
            { width: `${f.width}%`, background: f.color } as React.CSSProperties
          }
        >
          {f.label} · {f.value}
        </div>
      ))}
    </div>
  </div>
);
